/**
 * COMPLETE COUNTRY LIST HOOK
 *
 * Database-backed hook for accessing countries from the supported_countries table.
 * Provides consistent country data with search, filtering, and regional grouping capabilities.
 *
 * UPDATED: Now fetches from database instead of hardcoded list.
 */

import { useMemo, useCallback } from 'react';
import { useSupportedCountries, SupportedCountry } from './useSupportedCountries';
import { COMPLETE_COUNTRY_LIST } from '../data/completeCountryList';

// Re-export the interface for backward compatibility
export interface CompleteCountry {
 code: string;
 name: string;
 flag: string;
 region: 'Africa' | 'Asia' | 'Europe' | 'North America' | 'South America' | 'Oceania';
 subregion: string;
 currency: string;
 currencySymbol: string;
}

export interface CountryListOptions {
 includeRegions?: CompleteCountry['region'][];
 excludeRegions?: CompleteCountry['region'][];
 searchQuery?: string;
 sortBy?: 'name' | 'region' | 'code';
 prioritizeCountries?: string[]; // Country codes to show at top
}

// Convert SupportedCountry to CompleteCountry format for backward compatibility
function toCompleteCountry(sc: SupportedCountry): CompleteCountry {
 return {
 code: sc.country_code,
 name: sc.country_name,
 flag: sc.flag_emoji,
 region: 'Africa', // Database doesn't store region, default to Africa
 subregion: '',
 currency: '',
 currencySymbol: ''
 };
}

export interface UseCompleteCountryListReturn {
 // Core data
 allCountries: CompleteCountry[];
 totalCount: number;

 // Filtering functions
 getByRegion: (region: CompleteCountry['region']) => CompleteCountry[];
 getByCode: (code: string) => CompleteCountry | null;
 search: (query: string) => CompleteCountry[];

 // Grouped data
 countriesByRegion: Record<CompleteCountry['region'], CompleteCountry[]>;

 // Utility functions
 isValidCountryCode: (code: string) => boolean;
 getCountryName: (code: string) => string;
 getCountryFlag: (code: string) => string;

 // Formatted options for dropdowns
 countryOptions: Array<{ value: string; label: string; flag: string; region: string }>;
 regionOptions: Array<{ value: CompleteCountry['region']; label: string; count: number }>;

 // Loading state (new)
 loading?: boolean;
}

/**
 * Hook for accessing complete country list with filtering and search capabilities
 * NOW USES DATABASE instead of hardcoded list
 */
export const useCompleteCountryList = (options: CountryListOptions = {}): UseCompleteCountryListReturn => {
 const {
 searchQuery,
 sortBy = 'name',
 prioritizeCountries = []
 } = options;

 // Fetch countries from database
 const { countries: dbCountries, loading, getCountryByCode: getDbCountryByCode } = useSupportedCountries({
 searchQuery,
 prioritizeCountries
 });

 // Convert to CompleteCountry format for backward compatibility
 const filteredCountries = useMemo(() => {
 let countries = dbCountries.map(toCompleteCountry);

 // Sort countries
 countries.sort((a, b) => {
 switch (sortBy) {
 case 'region':
 return a.region.localeCompare(b.region) || a.name.localeCompare(b.name);
 case 'code':
 return a.code.localeCompare(b.code);
 case 'name':
 default:
 return a.name.localeCompare(b.name);
 }
 });

 // Prioritize specific countries (primary countries from DB are already prioritized)
 if (prioritizeCountries.length > 0) {
 const prioritized = countries.filter(country => prioritizeCountries.includes(country.code));
 const others = countries.filter(country => !prioritizeCountries.includes(country.code));

 prioritized.sort((a, b) => {
 const aIndex = prioritizeCountries.indexOf(a.code);
 const bIndex = prioritizeCountries.indexOf(b.code);
 return aIndex - bIndex;
 });

 countries = [...prioritized, ...others];
 }

 return countries;
 }, [dbCountries, sortBy, prioritizeCountries]);

 // Memoized countries grouped by region
 const countriesByRegion = useMemo(() => {
 const grouped: Record<CompleteCountry['region'], CompleteCountry[]> = {
 'Africa': [],
 'Asia': [],
 'Europe': [],
 'North America': [],
 'South America': [],
 'Oceania': []
 };

 filteredCountries.forEach(country => {
 grouped[country.region].push(country);
 });

 return grouped;
 }, [filteredCountries]);

 // Memoized dropdown options
 const countryOptions = useMemo(() => {
 return filteredCountries.map(country => ({
 value: country.code,
 label: country.name,
 flag: country.flag,
 region: country.region
 }));
 }, [filteredCountries]);

 // Memoized region options with counts
 const regionOptions = useMemo(() => {
 const regions: CompleteCountry['region'][] = ['Africa', 'Asia', 'Europe', 'North America', 'South America', 'Oceania'];
 
 return regions.map(region => ({
 value: region,
 label: region,
 count: countriesByRegion[region].length
 })).filter(region => region.count > 0);
 }, [countriesByRegion]);

 // Utility functions using database data
 const isValidCountryCode = useCallback((code: string): boolean => {
 return filteredCountries.some(country => country.code === code);
 }, [filteredCountries]);

 const getCountryName = useCallback((code: string): string => {
 if (!code) return 'Not set';
 // First try database
 const dbCountry = getDbCountryByCode(code);
 if (dbCountry) return dbCountry.country_name;
 // Fallback to hardcoded list
 const hardcodedCountry = COMPLETE_COUNTRY_LIST.find(c => c.code === code);
 return hardcodedCountry ? hardcodedCountry.name : code; // Return code if not found
 }, [getDbCountryByCode]);

 const getCountryFlag = useCallback((code: string): string => {
 if (!code) return '';
 // First try database
 const dbCountry = getDbCountryByCode(code);
 if (dbCountry) return dbCountry.flag_emoji;
 // Fallback to hardcoded list
 const hardcodedCountry = COMPLETE_COUNTRY_LIST.find(c => c.code === code);
 return hardcodedCountry ? hardcodedCountry.flag : '';
 }, [getDbCountryByCode]);

 const getByCode = useCallback((code: string): CompleteCountry | null => {
 const dbCountry = getDbCountryByCode(code);
 return dbCountry ? toCompleteCountry(dbCountry) : null;
 }, [getDbCountryByCode]);

 const getByRegion = useCallback((_region: CompleteCountry['region']): CompleteCountry[] => {
 // Since DB doesn't store region, return all countries
 return filteredCountries;
 }, [filteredCountries]);

 const search = useCallback((query: string): CompleteCountry[] => {
 if (!query) return filteredCountries;
 const q = query.toLowerCase();
 return filteredCountries.filter(c =>
 c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
 );
 }, [filteredCountries]);

 return {
 // Core data
 allCountries: filteredCountries,
 totalCount: filteredCountries.length,

 // Filtering functions
 getByRegion,
 getByCode,
 search,

 // Grouped data
 countriesByRegion,

 // Utility functions
 isValidCountryCode,
 getCountryName,
 getCountryFlag,

 // Formatted options
 countryOptions,
 regionOptions,

 // New: loading state
 loading
 };
};

/**
 * Hook specifically for African countries (prioritized for Aureus Africa)
 * Note: Now uses database which doesn't have region filtering - uses priority countries instead
 */
export const useAfricanCountries = () => {
 return useCompleteCountryList({
 prioritizeCountries: ['ZAF', 'NAM', 'SWZ', 'BWA', 'ZWE', 'MOZ', 'LSO'] // Southern Africa priority
 });
};

/**
 * Hook for countries supporting specific payment methods
 * Note: This would need to be enhanced with payment method data
 */
export const useCountriesWithPaymentSupport = (_paymentMethod?: 'crypto' | 'bank_transfer') => {
 // For now, return all countries - this can be enhanced with payment method filtering
 // when payment method data is added to the country list
 return useCompleteCountryList();
};

/**
 * Hook for popular/frequently selected countries
 */
export const usePopularCountries = () => {
 return useCompleteCountryList({
 prioritizeCountries: [
 'ZAF', // South Africa
 'USA', // United States 
 'GBR', // United Kingdom
 'CAN', // Canada
 'AUS', // Australia
 'DEU', // Germany
 'FRA', // France
 'NLD', // Netherlands
 'CHE', // Switzerland
 'SWE', // Sweden
 'NAM', // Namibia
 'SWZ', // Eswatini
 'BWA', // Botswana
 'IND', // India
 'CHN', // China
 'JPN', // Japan
 'BRA', // Brazil
 'MEX', // Mexico
 'NGA', // Nigeria
 'KEN' // Kenya
 ]
 });
};
